import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { AssetDto, FolderDto, GalleryDto } from '@picflow/types';
import { FinderComponent } from '../../../account-gallery-shared/components/finder/finder.component';
import { DialogOptions } from '../../../interfaces/dialog-data.interface';
import { AssetMoveService } from '../../../services/asset-move.service';
import { DialogService, OnGetData } from '../../../services/dialog.service';
import { ToastService } from '../../../services/toast.service';
import { LoadingIndicatorComponent } from '../../../shared/components/loading-indicator/loading-indicator.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';

@Component({
  selector: 'app-move-assets-dialog',
  templateUrl: './move-assets-dialog.component.html',
  styleUrls: ['./move-assets-dialog.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FinderComponent, ButtonDirective, LoadingIndicatorComponent],
})
export class MoveAssetsDialogComponent implements OnGetData {
  private check = inject(ChangeDetectorRef);
  private assetMoveService = inject(AssetMoveService);
  private toasts = inject(ToastService);
  private dialog = inject(DialogService);
  dialogName = 'move-assets';
  close: (...args) => void;
  assets: AssetDto[];
  gallery: GalleryDto;
  moveToGallery: GalleryDto;
  selectedFolder: FolderDto;
  loading = false;
  mode = 'move';

  dialogOptions(): DialogOptions {
    return { size: 640, spacingX: 40, spacingY: 40 };
  }

  async onGetData(
    data: {
      gallery: GalleryDto;
      moveToGallery: GalleryDto;
      selectedFolder: FolderDto;
      assets: AssetDto[];
      mode: typeof this.mode;
    },
    close: (...args) => void
  ) {
    this.close = close;
    this.gallery = data.gallery;
    this.moveToGallery = data.moveToGallery || this.gallery;
    this.selectedFolder = data.selectedFolder || undefined;
    this.assets = data.assets;
    this.changeMode(data.mode || 'move');
    this.check.detectChanges();
  }

  selectGallery(gallery: GalleryDto) {
    this.moveToGallery = gallery;
    this.check.detectChanges();
  }
  selectFolder(folder: FolderDto) {
    this.selectedFolder = folder;
    this.check.detectChanges();
  }

  changeMode(mode: 'move' | 'copy') {
    this.mode = mode;
    this.check.detectChanges();
  }

  async confirm() {
    if (this.loading) return;
    if (!this.moveToGallery) return;
    if (this.moveToGallery.id === this.gallery.id) {
      if (this.mode === 'copy') return this.toasts.add('You cannot copy assets within a gallery', 'error');
      return this.close(false);
    }
    this.loading = true;
    this.check.detectChanges();

    if (this.mode === 'move') {
      const resp = await this.assetMoveService.moveAssets(this.assets, this.moveToGallery);
      if (!resp) {
        this.loading = false;
        this.check.detectChanges();
        return;
      }
      this.toasts.add('Assets moved', 'success');
      this.close('moved');
    } else {
      const resp = await this.assetMoveService.copyAssets(this.assets, this.moveToGallery);
      if (!resp) {
        this.toasts.add('Not enough Storage remaining', 'error');
        this.loading = false;
        this.check.detectChanges();
        return;
      }
      this.toasts.add('Assets copied', 'success');
      this.close('copied');
    }
  }

  reOpenFn() {
    const { gallery, assets, mode, moveToGallery, selectedFolder } = this;
    return (_gallery: GalleryDto, _folder: FolderDto) => {
      this.dialog.open(MoveAssetsDialogComponent, {
        gallery: _folder ? undefined : gallery,
        assets,
        mode,
        moveToGallery: _folder ? undefined : _gallery || moveToGallery,
        selectedFolder: _folder || selectedFolder,
      });
    };
  }
}
